import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, X, Building, Hash, Layers, ChevronRight } from 'lucide-react';
import api from '@/utils/api';

// ALL AVAILABLE SERVICE CATEGORIES (from specification)
const ALL_SERVICE_CATEGORIES = [
  { name: 'Tax', key: 'Tax' },
  { name: 'DMV', key: 'DMV' },
  { name: 'Court', key: 'Court' },
  { name: 'Licenses', key: 'Licenses' },
  { name: 'Permits', key: 'Permits' },
  { name: 'Utilities', key: 'Utilities' },
  { name: 'Recreation', key: 'Recreation' },
  { name: 'eStore', key: 'eStore' },
  { name: 'Misc', key: 'Misc' },
  { name: 'Ticket', key: 'Ticket' },
  { name: 'Other', key: 'Other' }
];

/**
 * AccountCodeField Component (Enhanced with Subdimensions)
 * 
 * Auto-populates account codes based on customer/municipality name.
 * Shows two-level selection:
 * 1. First level: Service type (Tax, Utilities, etc.)
 * 2. Second level: Subdimension within that service (Income Tax, Property Tax, etc.)
 * 
 * The final account code is generated as: FIPS + Subdimension Code
 * 
 * Props:
 * - municipalityName: The customer/municipality name to look up
 * - selectedServiceType: The service type selected in the calculator (optional, for smart filtering)
 * - selectedState: The state code selected in the calculator (e.g., "GA", "MO")
 * - value: Currently selected account code
 * - onChange: Callback when account code is selected (code, details)
 * - disabled: Whether the field is disabled
 */
export default function AccountCodeField({ 
  municipalityName, 
  selectedServiceType,
  selectedState,
  value, 
  onChange, 
  disabled = false 
}) {
  // State for customer lookup
  const [customerFips, setCustomerFips] = useState(null);
  const [serviceOptions, setServiceOptions] = useState([]); // Available service types for this customer
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastLookedUpName = useRef('');

  // State for selection
  const [selectedService, setSelectedService] = useState(null);
  const [subdimensions, setSubdimensions] = useState([]);
  const [selectedSubdimension, setSelectedSubdimension] = useState(null);
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const [isSubdimensionDropdownOpen, setIsSubdimensionDropdownOpen] = useState(false);

  // State for generated account code
  const [generatedCode, setGeneratedCode] = useState('');
  
  // Validation state - warn if account code service doesn't match calculator service
  const [validationWarning, setValidationWarning] = useState(null);
  const [suggestedService, setSuggestedService] = useState(null);  // Suggested service to match calculator
  // Lookup customer data when municipality name changes
  const lookupCustomer = useCallback(async (name) => {
    if (!name || name.trim().length < 2) {
      setCustomerFips(null);
      setServiceOptions([]);
      setSelectedService(null);
      setSubdimensions([]);
      setSelectedSubdimension(null);
      setGeneratedCode('');
      setError(null);
      lastLookedUpName.current = '';
      // Clear the account code in parent
      onChange('', null);
      return;
    }

    const trimmedName = name.trim();
    if (trimmedName === lastLookedUpName.current) {
      return;
    }

    // Clear previous selections when municipality changes
    setSelectedService(null);
    setSubdimensions([]);
    setSelectedSubdimension(null);
    setGeneratedCode('');
    onChange('', null);

    setLoading(true);
    setError(null);
    lastLookedUpName.current = trimmedName;

    try {
      // NEW: Use entity-lookup endpoint for CSV-based lookup (36,034 entities)
      // Parse municipality name to extract entity name and state
      let entityName = trimmedName;
      let stateAbbrev = null;
      
      // Check if name includes state (e.g., "Arrow Rock, MO" or "Abbeville, GA")
      const parts = trimmedName.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        entityName = parts[0];
        stateAbbrev = parts[1].toUpperCase();
      }
      
      // PRIORITY: Use the selectedState prop if available (from calculator state dropdown)
      if (selectedState && selectedState.trim()) {
        stateAbbrev = selectedState.toUpperCase();
        console.log(`Using selectedState prop: ${stateAbbrev}`);
      }
      
      // If no state available, search broadly
      if (!stateAbbrev) {
        console.warn('No state provided - cannot lookup entity accurately');
      }
      
      // Call the new CSV-based entity lookup
      let response;
      if (stateAbbrev) {
        // Exact lookup with state
        response = await api.get(`/account-codes/entity-lookup/${encodeURIComponent(entityName)}`, {
          params: { state: stateAbbrev }
        });
        
        const data = response.data;
        
        // Handle case where multiple entities match (need disambiguation)
        if (data.matches && data.matches.length > 1) {
          // For now, use the first match (typically Municipality)
          const firstMatch = data.matches[0];
          setCustomerFips({
            customer: `${firstMatch.entity_name}, ${firstMatch.state_abbrev}`,
            clean_customer: firstMatch.entity_name,
            state: firstMatch.state_abbrev,
            entity_type: firstMatch.entity_type,
            fips: firstMatch.account_name  // This IS the FIPS code from CSV
          });
        } else if (data.entity_name) {
          // Single match
          setCustomerFips({
            customer: `${data.entity_name}, ${data.state_abbrev}`,
            clean_customer: data.entity_name,
            state: data.state_abbrev,
            entity_type: data.entity_type,
            fips: data.account_name  // This IS the FIPS code from CSV
          });
        }
        
        // Since CSV doesn't have service options pre-mapped, we allow all services
        // User selects service, then subdimension, then we generate the code
        setServiceOptions([]); // Will be populated on first service selection
        
      } else {
        // No state - search broadly
        response = await api.get('/account-codes/entity-search', {
          params: { q: entityName, limit: 1 }
        });
        
        const data = response.data;
        if (data.entities && data.entities.length > 0) {
          const firstEntity = data.entities[0];
          setCustomerFips({
            customer: `${firstEntity.entity_name}, ${firstEntity.state_abbrev}`,
            clean_customer: firstEntity.entity_name,
            state: firstEntity.state_abbrev,
            entity_type: firstEntity.entity_type,
            fips: firstEntity.account_name  // This IS the FIPS code from CSV
          });
          setServiceOptions([]);
        } else {
          setCustomerFips(null);
          setServiceOptions([]);
        }
      }
      
      // If selectedServiceType is provided from calculator, we can pre-load subdimensions
      if (selectedServiceType && customerFips) {
        await loadSubdimensions(selectedServiceType);
      }
      
    } catch (err) {
      console.error('Customer lookup failed:', err);
      setCustomerFips(null);
      setServiceOptions([]);
      if (err.response?.status !== 404) {
        setError('Failed to lookup customer');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedServiceType, selectedState]);

  // Load subdimensions for a service type
  const loadSubdimensions = async (serviceType) => {
    try {
      console.log('API call: /account-codes/service-groups/' + serviceType);
      const response = await api.get(`/account-codes/service-groups/${serviceType}`);
      const data = response.data;
      console.log('Subdimensions received:', data);
      
      if (data.sub_services && data.sub_services.length > 0) {
        setSubdimensions(data.sub_services);
        console.log('Subdimensions set:', data.sub_services.length);
        
        // If only one subdimension, auto-select it
        if (data.sub_services.length === 1) {
          handleSubdimensionSelect(data.sub_services[0]);
        }
      } else {
        console.warn('No sub_services found in response');
        setSubdimensions([]);
      }
    } catch (err) {
      console.error('Failed to load subdimensions:', err);
      console.error('Error details:', err.response?.data);
      setSubdimensions([]);
    }
  };

  // Handle service selection
  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    setSelectedSubdimension(null);
    setGeneratedCode('');
    setIsServiceDropdownOpen(false);
    setSubdimensions([]); // Clear subdimensions first
    
    // Load subdimensions for this service
    console.log('Loading subdimensions for service:', service.name);
    await loadSubdimensions(service.name);
  };

  // Handle subdimension selection
  const handleSubdimensionSelect = async (subdim) => {
    setSelectedSubdimension(subdim);
    setIsSubdimensionDropdownOpen(false);

    // Generate the account code
    if (customerFips && subdim) {
      try {
        const response = await api.get('/account-codes/generate', {
          params: {
            customer_name: customerFips.customer,
            service_code: subdim.code
          }
        });
        
        const data = response.data;
        setGeneratedCode(data.account_code);
        
        // Notify parent of the selection
        onChange(data.account_code, {
          ...data,
          service_type: selectedService?.name,
          sub_service: subdim.name,
          fips: customerFips.fips,
          customer: customerFips.customer
        });
      } catch (err) {
        console.error('Failed to generate account code:', err);
        // Fallback: generate locally
        const localCode = customerFips.fips 
          ? `${customerFips.fips}${subdim.code.padStart(4, '0')}`
          : `0${subdim.code.padStart(4, '0')}`;
        setGeneratedCode(localCode);
        onChange(localCode, {
          account_code: localCode,
          service_type: selectedService?.name,
          sub_service: subdim.name,
          fips: customerFips.fips || '0',
          service_code: subdim.code,
          customer: customerFips.customer
        });
      }
    }
  };

  // Clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedService(null);
    setSelectedSubdimension(null);
    setSubdimensions([]);
    setGeneratedCode('');
    onChange('', null);
  };

  // Effect to trigger lookup when municipality OR state changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      lookupCustomer(municipalityName);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [municipalityName, selectedState, lookupCustomer]); // Added selectedState!

  // Effect to handle selectedServiceType changes
  useEffect(() => {
    if (selectedServiceType && serviceOptions.length > 0 && !selectedService) {
      const matchingService = serviceOptions.find(
        s => s.name.toLowerCase() === selectedServiceType.toLowerCase()
      );
      if (matchingService) {
        handleServiceSelect(matchingService);
      }
    }
  }, [selectedServiceType, serviceOptions, selectedService]);

  // Validation effect - check if account code service matches calculator service
  useEffect(() => {
    if (selectedService && selectedServiceType) {
      // Normalize service names for comparison
      const normalizeServiceName = (name) => {
        if (!name) return '';
        const lower = name.toLowerCase().trim();
        // Map common variations
        const mappings = {
          'tax': 'tax',
          'taxes': 'tax',
          'tax collection': 'tax',
          'utility': 'utilities',
          'utilities': 'utilities',
          'utility billing': 'utilities',
          'court': 'court',
          'ticket': 'ticket',
          'tickets': 'ticket',
          'traffic tickets': 'ticket',
          'license': 'licenses',
          'licenses': 'licenses',
          'licensing': 'licenses',
          'permit': 'permits',
          'permits': 'permits',
          'building permits': 'permits',
          'misc': 'misc',
          'miscellaneous': 'misc',
          'estore': 'estore',
          'e-store': 'estore',
          'dmv': 'dmv'
        };
        return mappings[lower] || lower;
      };
      
      const selectedServiceNorm = normalizeServiceName(selectedService.name);
      const calculatorServiceNorm = normalizeServiceName(selectedServiceType);
      
      // Check if they match (allowing for some flexibility)
      const isMatch = selectedServiceNorm === calculatorServiceNorm ||
        selectedService.name.toLowerCase().includes(calculatorServiceNorm) ||
        calculatorServiceNorm.includes(selectedServiceNorm);
      
      if (!isMatch && generatedCode) {
        setValidationWarning(
          `Warning: Account Code service (${selectedService.name}) may not match the calculator service (${selectedServiceType}). Please verify.`
        );
        
        // Find suggested service that matches the calculator service
        const matchingService = serviceOptions.find(s => {
          const sNorm = normalizeServiceName(s.name);
          return sNorm === calculatorServiceNorm ||
            s.name.toLowerCase().includes(calculatorServiceNorm) ||
            calculatorServiceNorm.includes(sNorm);
        });
        
        if (matchingService && matchingService.name !== selectedService.name) {
          setSuggestedService(matchingService);
        } else {
          setSuggestedService(null);
        }
      } else {
        setValidationWarning(null);
        setSuggestedService(null);
      }
    } else {
      setValidationWarning(null);
      setSuggestedService(null);
    }
  }, [selectedService, selectedServiceType, generatedCode, serviceOptions]);

  // Effect to regenerate account code when FIPS changes (e.g., municipality changes)
  // but service/subdimension selections remain
  useEffect(() => {
    const regenerateCode = async () => {
      if (customerFips && selectedSubdimension && selectedService) {
        try {
          const response = await api.get('/account-codes/generate', {
            params: {
              customer_name: customerFips.customer,
              service_code: selectedSubdimension.code
            }
          });
          
          const data = response.data;
          setGeneratedCode(data.account_code);
          
          // Notify parent of the updated code
          onChange(data.account_code, {
            ...data,
            service_type: selectedService.name,
            sub_service: selectedSubdimension.name,
            fips: customerFips.fips,
            customer: customerFips.customer
          });
        } catch (err) {
          console.error('Failed to regenerate account code:', err);
          // Fallback: generate locally
          const localCode = customerFips.fips 
            ? `${customerFips.fips}${selectedSubdimension.code.padStart(4, '0')}`
            : `0${selectedSubdimension.code.padStart(4, '0')}`;
          setGeneratedCode(localCode);
          onChange(localCode, {
            account_code: localCode,
            service_type: selectedService.name,
            sub_service: selectedSubdimension.name,
            fips: customerFips.fips || '0',
            service_code: selectedSubdimension.code,
            customer: customerFips.customer
          });
        }
      }
    };

    regenerateCode();
  }, [customerFips?.fips]); // Only regenerate when FIPS changes

  // Handle clicking on suggestion to auto-correct
  const handleUseSuggestion = async () => {
    if (suggestedService) {
      await handleServiceSelect(suggestedService);
    }
  };

  return (
    <div className="space-y-3">
      {/* Main Account Code Display */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Account Code
          <span className="ml-2 text-xs text-slate-500">(FIPS Code + Service Code)</span>
        </label>
        
        <div 
          className={`
            input-field flex items-center justify-between
            ${disabled ? 'bg-slate-100 cursor-not-allowed' : 'bg-blue-50'}
            ${generatedCode ? 'border-green-400' : ''}
          `}
          data-testid="account-code-field"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Hash className="w-4 h-4 text-slate-400 flex-shrink-0" />
            
            {loading ? (
              <span className="text-slate-400 italic text-sm">Looking up customer...</span>
            ) : generatedCode ? (
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-sm font-medium text-slate-800 truncate">
                  {generatedCode}
                </span>
                {selectedSubdimension && (
                  <span className="text-xs text-slate-500 bg-green-100 px-2 py-0.5 rounded">
                    {selectedSubdimension.name}
                  </span>
                )}
              </div>
            ) : customerFips ? (
              <span className="text-slate-500 text-sm">Select service & subdimension below</span>
            ) : municipalityName ? (
              <span className="text-slate-400 text-sm italic">No matching customer found</span>
            ) : (
              <span className="text-slate-400 text-sm">Enter municipality to lookup</span>
            )}
          </div>

          {generatedCode && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-slate-200 rounded"
              data-testid="account-code-clear"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      </div>

      {/* FIPS Code & Account Code Formula Display */}
      {customerFips && (
        <div className="text-xs text-slate-600 bg-slate-50 px-3 py-2.5 rounded border border-slate-200 space-y-1.5">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1">
              <Building className="w-3 h-3 text-slate-400" />
              <span className="font-medium">Customer:</span> 
              <span className="text-slate-700">{customerFips.clean_customer}</span>
            </div>
            <div>
              <span className="font-medium">State:</span> 
              <span className="text-slate-700">{customerFips.state}</span>
            </div>
            <div>
              <span className="font-medium">Entity Type:</span> 
              <span className="text-slate-700 font-mono">{customerFips.entity_type || 'MU'}</span>
              <span className="text-slate-400 ml-1">
                ({customerFips.entity_type === 'MU' ? 'Municipality' : 
                  customerFips.entity_type === 'CO' ? 'County' : 
                  customerFips.entity_type === 'SC' ? 'School' : 
                  customerFips.entity_type === 'ST' ? 'State' : 'Other'})
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">FIPS Code:</span> 
            {customerFips.fips ? (
              <>
                <code className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-mono font-semibold">
                  {customerFips.fips}
                </code>
                <span className="text-slate-400 text-xs">
                  (State: {customerFips.state} + Type: {customerFips.entity_type || 'MU'} + FIPS Digits)
                </span>
              </>
            ) : (
              <>
                <code className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-mono font-semibold">
                  0
                </code>
                <span className="text-amber-600 text-xs">
                  (No FIPS assigned - Service Code will be zero-padded to 5 digits)
                </span>
              </>
            )}
          </div>
          {generatedCode && selectedSubdimension && (
            <div className="pt-1.5 border-t border-slate-200">
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="font-medium text-slate-700">Account Code Formula:</span>
                <span className="text-slate-500">FIPS Code</span>
                <span className="text-slate-400">+</span>
                <span className="text-slate-500">Service Code</span>
                <span className="text-slate-400">=</span>
                <code className="text-xs font-mono text-slate-600 bg-slate-100 px-1 rounded">
                  {customerFips.fips || '0'}
                </code>
                <span className="text-slate-400">+</span>
                <code className="text-xs font-mono text-slate-600 bg-slate-100 px-1 rounded">
                  {selectedSubdimension.code}
                </code>
                <span className="text-slate-400">=</span>
                <code className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-mono font-bold">
                  {generatedCode}
                </code>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Service Type Selection */}
      {customerFips && (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            <Layers className="w-3 h-3 inline mr-1" />
            Service Category
          </label>
          <div className="relative">
            <div
              className={`
                input-field flex items-center justify-between cursor-pointer
                ${disabled ? 'bg-slate-100 cursor-not-allowed' : 'bg-yellow-50 hover:bg-yellow-100'}
                ${isServiceDropdownOpen ? 'ring-2 ring-primary ring-offset-1' : ''}
              `}
              onClick={() => !disabled && setIsServiceDropdownOpen(!isServiceDropdownOpen)}
              data-testid="service-type-selector"
            >
              <span className={selectedService ? 'font-medium text-slate-800' : 'text-slate-500'}>
                {selectedService ? selectedService.name : 'Select service category...'}
              </span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isServiceDropdownOpen ? 'rotate-180' : ''}`} />
            </div>

            {isServiceDropdownOpen && (
              <div 
                className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg border border-slate-200 max-h-48 overflow-auto"
                data-testid="service-type-dropdown"
              >
                {ALL_SERVICE_CATEGORIES.map((service, idx) => (
                  <div
                    key={service.name}
                    className={`
                      px-3 py-2 cursor-pointer flex items-center justify-between
                      ${selectedService?.name === service.name 
                        ? 'bg-primary/10 border-l-2 border-primary' 
                        : 'hover:bg-slate-50 border-l-2 border-transparent'}
                    `}
                    onClick={() => handleServiceSelect(service)}
                    data-testid={`service-option-${idx}`}
                  >
                    <div>
                      <div className="font-medium text-sm text-slate-800">{service.name}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Subdimension Selection */}
      {selectedService && (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            <Hash className="w-3 h-3 inline mr-1" />
            Sub-Service / Dimension
            {subdimensions.length === 1 && (
              <span className="ml-2 text-slate-400">(Auto-selected)</span>
            )}
          </label>
          <div className="relative">
            <div
              className={`
                input-field flex items-center justify-between
                ${disabled ? 'bg-slate-100 cursor-not-allowed' : subdimensions.length === 0 ? 'bg-slate-50' : 'bg-green-50 hover:bg-green-100 cursor-pointer'}
                ${isSubdimensionDropdownOpen ? 'ring-2 ring-green-500 ring-offset-1' : ''}
              `}
              onClick={() => !disabled && subdimensions.length > 1 && setIsSubdimensionDropdownOpen(!isSubdimensionDropdownOpen)}
              data-testid="subdimension-selector"
            >
              <div className="flex-1">
                {subdimensions.length === 0 ? (
                  <span className="text-slate-400 text-sm italic">Loading sub-services...</span>
                ) : selectedSubdimension ? (
                  <div>
                    <span className="font-medium text-slate-800">{selectedSubdimension.name}</span>
                    <span className="ml-2 text-xs text-slate-500 font-mono">({selectedSubdimension.code})</span>
                  </div>
                ) : (
                  <span className="text-slate-500">Select sub-service...</span>
                )}
              </div>
              {subdimensions.length > 1 && (
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isSubdimensionDropdownOpen ? 'rotate-180' : ''}`} />
              )}
            </div>

            {isSubdimensionDropdownOpen && subdimensions.length > 1 && (
              <div 
                className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg border border-slate-200 max-h-60 overflow-auto"
                data-testid="subdimension-dropdown"
              >
                <div className="p-2 border-b border-slate-100 bg-slate-50">
                  <p className="text-xs text-slate-500">
                    Select specific {selectedService.name} sub-service
                  </p>
                </div>
                {subdimensions.map((subdim, idx) => (
                  <div
                    key={subdim.code}
                    className={`
                      px-3 py-2 cursor-pointer
                      ${selectedSubdimension?.code === subdim.code 
                        ? 'bg-green-100 border-l-2 border-green-500' 
                        : 'hover:bg-slate-50 border-l-2 border-transparent'}
                    `}
                    onClick={() => handleSubdimensionSelect(subdim)}
                    data-testid={`subdimension-option-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm text-slate-800">{subdim.name}</div>
                        <div className="text-xs text-slate-500">{subdim.description}</div>
                      </div>
                      <span className="font-mono text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                        {subdim.code}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Info Display */}
      {customerFips && (
        <div className="text-xs text-slate-500 flex items-center gap-2 bg-slate-50 px-3 py-2 rounded">
          <Building className="w-3 h-3" />
          <span>
            <strong>FIPS:</strong> {customerFips.fips || 'N/A'} | 
            <strong className="ml-2">State:</strong> {customerFips.state} | 
            <strong className="ml-2">Type:</strong> {customerFips.entity_type || 'N/A'}
          </span>
        </div>
      )}

      {/* Validation Warning */}
      {validationWarning && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded" data-testid="account-code-validation-warning">
          <div className="flex items-start gap-2">
            <span className="text-amber-500 font-bold">⚠</span>
            <span>{validationWarning}</span>
          </div>
          
          {/* Auto-Correction Suggestion */}
          {suggestedService && (
            <div className="mt-2 pt-2 border-t border-amber-200">
              <button
                type="button"
                onClick={handleUseSuggestion}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 underline flex items-center gap-1"
                data-testid="auto-correct-suggestion"
              >
                <span>✨</span>
                <span>Auto-fix: Switch to "{suggestedService.name}" service?</span>
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
